import React, { useState } from "react";

interface Section {
  id: string;
  label: string;
  risk: string;
  notes: string;
  evidence: string;
}

interface PreAdmission {
  id: string;
  residentName: string;
  referralDate: string;
  source: string;
  sections: Section[];
  canMeet: string;
  supportNeeded: string;
  decision: string;
  approvedBy: string;
  approvedDate: string;
  bundleCreated: boolean;
}

const KEY = "enhanced_admissions";
const BUNDLE_KEY = "admissions_bundle";

const SECTION_DEFS = [
  "Medical history & diagnoses",
  "Cognition & mental capacity",
  "Behaviour & risk to self/others",
  "Mobility, falls & moving/handling",
  "Nutrition, swallowing & hydration",
  "Skin integrity & tissue viability",
  "Continence",
  "Medication complexity (CDs, covert, PRN)",
  "Infection status & IPC considerations",
  "End-of-life / palliative needs",
  "Safeguarding history",
  "Social, family & communication",
];

const PRE = ["Funding confirmed & agreed rate", "Contract signed & consent obtained", "GP registration confirmed", "Pharmacy registered", "Room ready & first-night plan", "Pre-admission visit completed"];
const H72 = ["Waterlow skin assessment", "Falls risk assessment", "MUST nutrition assessment", "Skin integrity check", "Mental health & cognition baseline", "Capacity / MCA screening", "Medication review & MAR started", "Personalised care plan created"];

const load = (): PreAdmission[] => {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
};

const blank = (name: string): PreAdmission => ({
  id: `pa-${Date.now()}`,
  residentName: name,
  referralDate: new Date().toISOString().slice(0, 10),
  source: "Local Authority",
  sections: SECTION_DEFS.map(label => ({ id: label, label, risk: "", notes: "", evidence: "" })),
  canMeet: "",
  supportNeeded: "",
  decision: "Pending",
  approvedBy: "",
  approvedDate: "",
  bundleCreated: false,
});

export default function EnhancedAdmissions({ user }: { user?: any }) {
  const [records, setRecords] = useState<PreAdmission[]>(load);
  const [form, setForm] = useState<PreAdmission | null>(null);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");

  const save = (next: PreAdmission[]) => {
    setRecords(next);
    localStorage.setItem(KEY, JSON.stringify(next));
  };

  const start = () => {
    if (!name) {
      setMessage("Enter the prospective resident's name.");
      return;
    }
    setForm(blank(name));
    setName("");
  };

  const setSection = (id: string, patch: Partial<Section>) => {
    setForm(f => (f ? { ...f, sections: f.sections.map(s => (s.id === id ? { ...s, ...patch } : s)) } : f));
  };

  const gaps = (f: PreAdmission) => {
    const g: string[] = [];
    f.sections.forEach(s => {
      if (!s.risk) g.push(s.label);
    });
    if (!f.canMeet) g.push("Can-meet-needs decision");
    if (f.decision === "Accepted" && !f.approvedBy) g.push("Registered Manager approval");
    return g;
  };

  const saveRecord = () => {
    if (!form) return;
    const g = gaps(form);
    if (g.length) {
      setMessage(`Cannot save - complete: ${g.slice(0, 4).join("; ")}${g.length > 4 ? ` (+${g.length - 4} more)` : ""}. Zero holes: every domain must be assessed.`);
      return;
    }
    const rec = { ...form, approvedDate: form.decision === "Accepted" ? new Date().toISOString().slice(0, 10) : form.approvedDate };
    let bundleCreated = rec.bundleCreated;
    if (rec.decision === "Accepted" && !bundleCreated) {
      let bundles: any[] = [];
      try { bundles = JSON.parse(localStorage.getItem(BUNDLE_KEY) || "[]"); } catch { bundles = []; }
      bundles = [{ id: `adm-${Date.now()}`, residentName: rec.residentName, admissionDate: rec.referralDate, items: [...PRE.map((label, i) => ({ key: `pre-${i}`, label, stage: "pre", done: false, doneDate: "", doneBy: "" })), ...H72.map((label, i) => ({ key: `72-${i}`, label, stage: "72", done: false, doneDate: "", doneBy: "" }))] }, ...bundles];
      localStorage.setItem(BUNDLE_KEY, JSON.stringify(bundles));
      bundleCreated = true;
    }
    save([{ ...rec, bundleCreated }, ...records.filter(r => r.id !== rec.id)]);
    setForm(null);
    setMessage(rec.decision === "Accepted" ? "Admission accepted - 72-hour bundle auto-created." : "Pre-admission assessment saved.");
  };

  const remove = (id: string) => save(records.filter(r => r.id !== id));

  const printAssessment = (r: PreAdmission) => {
    const rows = r.sections.map(s => `<tr><td style="padding:6px;border-bottom:1px solid #eee;font-weight:bold;width:35%">${s.label}</td><td style="padding:6px;border-bottom:1px solid #eee;color:${s.risk === "High" ? "#dc2626" : s.risk === "Medium" ? "#d97706" : "#16a34a"};font-weight:bold">${s.risk}</td><td style="padding:6px;border-bottom:1px solid #eee">${s.notes || "-"}</td><td style="padding:6px;border-bottom:1px solid #eee">${s.evidence || "-"}</td></tr>`).join("");
    const html = `<!DOCTYPE html><html><head><title>Pre-Admission Risk Assessment</title><style>body{font-family:Arial;margin:40px;color:#1f2937}h2{color:#123B36}table{width:100%;border-collapse:collapse;font-size:13px}</style></head><body><h2>Pre-Admission Risk Assessment - Roop Cottage</h2><p><strong>Prospective resident:</strong> ${r.residentName} | Referral ${r.referralDate} from ${r.source}</p><p><strong>Can meet needs:</strong> ${r.canMeet} ${r.supportNeeded ? `- ${r.supportNeeded}` : ""} | <strong>Decision:</strong> ${r.decision} ${r.approvedBy ? `- approved by ${r.approvedBy} ${r.approvedDate}` : ""}</p><table><tr><th>Domain</th><th>Risk</th><th>Assessment notes</th><th>Evidence source</th></tr>${rows}</table><p style="margin-top:20px;font-size:11px;color:#6b7280">Completed before admission in compliance with CQC Regulation 9 & 12 and the Admissions finding of the latest inspection.</p></body></html>`;
    const win = window.open("", "_blank");
    if (win) { win.document.write(html); win.document.close(); win.focus(); win.print(); }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">Enhanced Admissions - Pre-Admission Risk Assessment</h2>
        <p className="text-sm text-slate-600">No admission is accepted until all twelve risk domains are assessed, the can-meet decision is recorded and the Registered Manager signs it off. Acceptance auto-creates the 72-hour bundle.</p>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      {form ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 font-semibold text-slate-900">Pre-admission assessment - {form.residentName}</h3>
          <div className="mb-3 grid gap-3 md:grid-cols-3">
            <input type="date" value={form.referralDate} onChange={e => setForm(f => (f ? { ...f, referralDate: e.target.value } : f))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
            <select value={form.source} onChange={e => setForm(f => (f ? { ...f, source: e.target.value } : f))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
              <option>Local Authority</option><option>NHS / Hospital</option><option>Family</option><option>Self-referral</option><option>Other</option>
            </select>
            <select value={form.decision} onChange={e => setForm(f => (f ? { ...f, decision: e.target.value } : f))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
              <option>Pending</option><option>Accepted</option><option>Declined</option>
            </select>
          </div>
          <div className="space-y-2">
            {form.sections.map(s => (
              <div key={s.id} className="rounded-lg border border-slate-100 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm font-medium text-slate-900">{s.label}</div>
                  <div className="flex gap-1">
                    {["Low", "Medium", "High"].map(r => (
                      <button key={r} onClick={() => setSection(s.id, { risk: r })} className={s.risk === r ? `rounded px-3 py-1 text-xs font-semibold text-white ${r === "High" ? "bg-red-600" : r === "Medium" ? "bg-amber-500" : "bg-green-600"}` : "rounded bg-slate-100 px-3 py-1 text-xs text-slate-600"}>{r}</button>
                    ))}
                  </div>
                </div>
                <div className="mt-2 grid gap-2 md:grid-cols-2">
                  <input value={s.notes} onChange={e => setSection(s.id, { notes: e.target.value })} placeholder="Assessment notes" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                  <input value={s.evidence} onChange={e => setSection(s.id, { evidence: e.target.value })} placeholder="Evidence source (GP records, hospital letter, visit...)" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 grid gap-3 md:grid-cols-3">
            <select value={form.canMeet} onChange={e => setForm(f => (f ? { ...f, canMeet: e.target.value } : f))} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
              <option value="">Can we meet this person's needs?</option>
              <option>Yes</option><option>Yes with support</option><option>No</option>
            </select>
            <input value={form.supportNeeded} onChange={e => setForm(f => (f ? { ...f, supportNeeded: e.target.value } : f))} placeholder="Support needed (e.g. 2:1, SALT, district nursing)" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
            <input value={form.approvedBy} onChange={e => setForm(f => (f ? { ...f, approvedBy: e.target.value } : f))} placeholder="Approved by (Registered Manager)" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={saveRecord} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">Save assessment</button>
            <button onClick={() => setForm(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <h3 className="mb-3 font-semibold text-slate-900">Start a pre-admission assessment</h3>
          <div className="flex flex-wrap gap-2">
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Prospective resident name" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
            <button onClick={start} className="rounded-lg bg-blue-900 px-4 py-2 text-sm font-medium text-white">Start assessment</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {records.map(r => (
          <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-sm font-medium text-slate-900">{r.residentName} <span className="text-xs text-slate-500">referral {r.referralDate} from {r.source}</span></div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r.decision === "Accepted" ? "bg-green-100 text-green-700" : r.decision === "Declined" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{r.decision}</span>
                <span className="text-xs text-slate-600">{r.sections.filter(s => s.risk === "High").length} high risk</span>
                <button onClick={() => setForm(r)} className="rounded-lg border border-slate-300 px-2 py-1 text-xs">Edit</button>
                <button onClick={() => printAssessment(r)} className="rounded-lg border border-slate-300 px-2 py-1 text-xs">Print</button>
                <button onClick={() => remove(r.id)} className="text-xs text-red-600">Remove</button>
              </div>
            </div>
            <div className="mt-1 text-xs text-slate-600">Can meet: {r.canMeet || "-"} | Approved by {r.approvedBy || "-"}{r.bundleCreated ? " | 72h bundle created" : ""}</div>
          </div>
        ))}
        {records.length === 0 ? <div className="rounded-lg bg-slate-50 p-4 text-sm text-slate-500">No pre-admission assessments yet. Every new referral must start here.</div> : null}
      </div>
    </div>
  );
}